// ---------------------------------------------------------------------------
// blog.ts — the register of every article under /blog, in one place.
//
// C1 / M5 part 1 (the founder's brief, 2026-10-10): a Blog section whose
// articles can be found from a Google search, starting with the two comparison
// pages the marketing plan rated as the highest-intent searches on its list —
// "ReCurr vs Stripe Billing" and "ReCurr vs Chargebee". The SEO record of
// 2026-10-10 (docs/ReCurr App/seo-and-search-console-2026-10-10.md §5) found
// that one FAQ answer on the home page was the only text on the site that
// mentioned either name.
//
// WHY A REGISTER AND NOT A FOLDER OF PAGES. Every public URL on this site
// needs five edits (config/domains' MARKETING_PREFIXES, the route, a PageSeo
// entry AND a SITEMAP_PATHS entry in config/seo.ts, a robots.txt check, and a
// link from somewhere). The prefix, the route and the robots check are done
// ONCE for `/blog`; the per-article half — the title, the description, the
// canonical and the sitemap line — is derived from this list, so an article
// cannot be published without its head, and cannot get a head without being in
// the sitemap. `__tests__/blog.test.tsx` holds the two halves together: every
// entry here has a body in content/blog, and every body has an entry here.
//
// THE METADATA LIVES APART FROM THE BODIES ON PURPOSE. config/seo.ts imports
// this file to build titles and the sitemap at BUILD time (the recurrSitemap
// plugin in vite.config.ts), and a build-time import must not drag React
// components in with it. The bodies are in src/content/blog/, keyed by slug.
//
// ⚠ A COMPARISON IS A LIST OF CLAIMS ABOUT SOMEBODY ELSE'S PRODUCT, and those
// claims go stale without anything in this repository noticing — the same shape
// as the plugin column in config/pluginComparison.ts. So every article carries
// a `checkedOn` date and names its sources, and the page prints both. When a
// competitor's price or availability changes, the article is corrected and
// `checkedOn` moves; it is never left to read as current by default.
//
// ⛔ THE FOUNDER'S STANDING RULE BINDS EVERY ARTICLE: no promise about ReCurr's
// future pricing or availability, no merchant count, no testimonial. The
// forbidden-phrase guard in marketing-proof.test.tsx renders every article.
// ---------------------------------------------------------------------------

import type { IconName } from '../components/icons';

export const BLOG_PATH = '/blog';

export interface BlogArticleMeta {
  /** The URL segment after /blog/. Lowercase, hyphenated, permanent once published. */
  slug: string;
  /** The <h1>, and the headline in the BlogPosting structured data. */
  title: string;
  /** The <title>. Under 65 characters or a search result truncates it. */
  seoTitle: string;
  /** meta[name=description]. Under 160 characters — see config/seo.ts. */
  description: string;
  /** The standfirst under the title. Longer than the description; says what the article will and will not do. */
  lead: string;
  /** One or two sentences for the card on /blog. */
  summary: string;
  /** ISO dates. `updatedOn` moves when the TEXT changes; `checkedOn` when the FACTS were last verified. */
  publishedOn: string;
  updatedOn: string;
  checkedOn: string;
  readingMinutes: number;
  /** Shown on the card and published as BlogPosting.keywords. */
  tags: string[];
  /** The small label above the title — what kind of article this is. */
  kind: string;
  icon: IconName;
}

export const BLOG_ARTICLES: BlogArticleMeta[] = [
  {
    slug: 'recurr-vs-stripe-billing-south-africa',
    title: 'ReCurr vs Stripe Billing for South African businesses',
    seoTitle: 'ReCurr vs Stripe Billing in South Africa — an honest comparison',
    description:
      'Stripe Billing is excellent, and mostly out of reach for a South African company. What the Stripe route really is here, what each costs, and where ReCurr fits.',
    lead:
      'Stripe Billing is what most people mean when they say "subscription billing". Here’s what it is, what ' +
      'it costs, why a South African business usually can’t sign up for it, and what ReCurr does instead, ' +
      'including where Stripe is simply better.',
    summary:
      'Stripe lists South Africa only as an "extended network" market served through Paystack, and Stripe Billing ' +
      'charges a percentage of everything you bill. What that means for a business that bills in rand, and where ReCurr fits.',
    publishedOn: '2026-10-10',
    updatedOn: '2026-10-10',
    checkedOn: '2026-10-10',
    readingMinutes: 9,
    tags: ['Stripe Billing alternative', 'subscription billing South Africa', 'PayFast recurring payments'],
    kind: 'Comparison',
    icon: 'scales',
  },
  {
    slug: 'recurr-vs-chargebee-south-africa',
    title: 'ReCurr vs Chargebee for South African businesses',
    seoTitle: 'ReCurr vs Chargebee in South Africa — an honest comparison',
    description:
      'Chargebee takes South African companies but not PayFast accounts. What its pricing means in rand, which gateways it offers here, and where ReCurr fits.',
    lead:
      'Chargebee is a mature subscription-management platform, and a South African company can sign up for it today. ' +
      'The useful questions are which gateway you can charge through here, what a percentage of invoicing volume ' +
      'costs in rand, and what you’d give up by paying a flat fee instead.',
    summary:
      'Chargebee charges 0.65–0.80% of monthly invoicing volume and, in South Africa, documents Paystack (cards only) ' +
      'and one partner gateway in private beta — not PayFast. What that means, and where ReCurr fits.',
    publishedOn: '2026-10-10',
    updatedOn: '2026-10-10',
    checkedOn: '2026-10-10',
    readingMinutes: 8,
    tags: ['Chargebee alternative', 'subscription billing South Africa', 'PayFast recurring payments'],
    kind: 'Comparison',
    icon: 'scales',
  },
];

/** The article for a slug, or undefined — an unknown slug is the 404 page, never a fabricated article. */
export function blogArticleBySlug(slug: string): BlogArticleMeta | undefined {
  return BLOG_ARTICLES.find((a) => a.slug === slug);
}

/** The path an article is served at. One place, so a link and the sitemap cannot disagree. */
export function blogArticlePath(slug: string): string {
  return `${BLOG_PATH}/${slug}`;
}

/** Every article path, in the order the index lists them — newest first. */
export function blogArticlePaths(): string[] {
  return sortedArticles().map((a) => blogArticlePath(a.slug));
}

/** Newest first, by `publishedOn`; a tie keeps register order. */
export function sortedArticles(): BlogArticleMeta[] {
  return [...BLOG_ARTICLES].sort((a, b) => (a.publishedOn < b.publishedOn ? 1 : a.publishedOn > b.publishedOn ? -1 : 0));
}

/** "10 October 2026", for the byline. en-ZA, like everything else the site prints. */
export function formatArticleDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-ZA', {
    day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
  });
}
